import React, { useRef, useState } from 'react';
import {
	Box,
	Button,
	Paper,
	Typography,
	Chip,
	LinearProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Divider,
	Grid,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, PendingActions as PendingIcon } from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';
import { OnboardingPhaseTwoDocumentStatus, uploadOnboardingPhaseTwoDocument } from 'core/API/onboarding';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

function getPdfUrl(path: string): string {
	return `${process.env.REACT_APP_IMAGE_URL}/onboarding_templates/${path}`;
}

function getFilledPdfUrl(path: string): string {
	return `${process.env.REACT_APP_IMAGE_URL}/onboarding_employee/${path}`;
}

interface OnboardingDocumentViewerProps {
	documentStatuses: OnboardingPhaseTwoDocumentStatus[];
	documentTemplates: IOnboardingDocument[];
	onboardingId: number;
	onRefresh: () => Promise<void>;
	onAllDocumentsComplete: () => void;
}

export function OnboardingDocumentViewer({
	documentStatuses,
	documentTemplates,
	onboardingId,
	onRefresh: _onRefresh,
	onAllDocumentsComplete,
}: OnboardingDocumentViewerProps): JSX.Element {
	const viewer = useRef<HTMLDivElement>(null);
	const instance = useRef<any>(null);
	const isInitializing = useRef<boolean>(false);
	// Default to first pending document, or 0 if all are completed
	const [currentDocIndex, setCurrentDocIndex] = useState(() => {
		const firstPending = documentStatuses.findIndex(
			(doc) => doc.status !== 'completed' && doc.status !== 'approved'
		);
		return firstPending !== -1 ? firstPending : 0;
	});
	const [isUploading, setIsUploading] = useState(false);
	const [isViewerReady, setIsViewerReady] = useState(false);
	const { showToast } = useToastContext();

	// Keep a local copy of document statuses so we can update without full reload
	const [localDocStatuses, setLocalDocStatuses] = useState(documentStatuses || []);

	// Sync from parent if the prop changes (e.g. on initial load)
	React.useEffect(() => {
		if (documentStatuses?.length) {
			setLocalDocStatuses(documentStatuses);
		}
	}, [documentStatuses]);

	// Map documentStatuses to their corresponding templates
	const documents = localDocStatuses;
	const currentStatus = documents[currentDocIndex];
	const currentTemplate = currentStatus
		? documentTemplates.find((template) => template.id === currentStatus.template_id)
		: null;

	// For completed docs, show the filled PDF; for pending, show the template
	const getDocPdfUrl = (docStatus: OnboardingPhaseTwoDocumentStatus): string | null => {
		if (
			(docStatus.status === 'completed' || docStatus.status === 'approved') &&
			docStatus.path
		) {
			return getFilledPdfUrl(docStatus.path);
		}
		const template = documentTemplates.find((t) => t.id === docStatus.template_id);
		return template?.filePath ? getPdfUrl(template.filePath) : null;
	};

	const pdfUrl = currentStatus ? getDocPdfUrl(currentStatus) : null;

	React.useEffect(() => {
		console.log('Initialization check:', {
			hasViewer: !!viewer.current,
			hasInstance: !!instance.current,
			isInitializing: isInitializing.current,
			childrenLength: viewer.current?.children.length,
		});
		
		// Prevent double initialization - check instance ref, isInitializing flag, and DOM children
		if (viewer.current && !instance.current && !isInitializing.current) {
			console.log('Starting WebViewer initialization...', { pdfUrl });
			isInitializing.current = true;
			
			WebViewer(
				{
					path: `/webviewer`,
					licenseKey:
						'demo:1770900361581:6082a73403000000008bfdc578db599d0904b024b2339e144c1e745ec5',
					initialDoc: pdfUrl || undefined,
					fullAPI: true,
					disableStreaming: true,
				} as any,
				viewer.current
			).then((inst) => {
				console.log('WebViewer promise resolved', inst);
				instance.current = inst;
				setIsViewerReady(true);
				console.log('WebViewer initialized successfully - viewer ready');
				
				// Set default zoom to 100% after document loads
				inst.Core.documentViewer.addEventListener('documentLoaded', () => {
					inst.UI.setZoomLevel(1);
				});
				
				// Load the document if pdfUrl is available
				if (pdfUrl) {
					inst.UI.loadDocument(pdfUrl);
				}
			}).catch((error) => {
				console.error('WebViewer initialization error:', error);
				showToast('error', 'Failed to initialize PDF viewer');
				isInitializing.current = false;
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (instance.current && pdfUrl && isViewerReady) {
			console.log('Loading document:', pdfUrl);
			// Just load the document, WebViewer will show its own loader
			instance.current.UI.loadDocument(pdfUrl);
			// Zoom will be set automatically by the documentLoaded event listener
		}
	}, [pdfUrl, isViewerReady]);

	// Download handler - hidden for now, kept for future use
	const _handleDownload = async () => {
		if (!instance.current) {
			showToast('error', 'PDF viewer not initialized');
			console.error('Download failed: instance.current is null');
			return;
		}

		try {
			const { documentViewer, annotationManager } = instance.current.Core;
			const doc = documentViewer.getDocument();
			
			if (!doc) {
				showToast('error', 'No document loaded');
				console.error('Download failed: No document loaded');
				return;
			}

			// Get the document data with filled form fields and annotations
			const data = await doc.getFileData({ xfdfString: await annotationManager.exportAnnotations() });
			const arr = new Uint8Array(data);
			const blob = new Blob([arr], { type: 'application/pdf' });
			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = `${currentStatus?.name || 'document'}.pdf`;
			link.click();
			showToast('success', 'Document downloaded successfully');
		} catch (error) {
			showToast('error', 'Failed to download document');
			console.error('Download error:', error);
		}
	};

	const handleMarkAsComplete = async () => {
		if (!instance.current) {
			showToast('error', 'PDF viewer not initialized');
			console.error('Mark complete failed: instance.current is null');
			return;
		}

		if (!currentStatus || !currentTemplate) {
			showToast('error', 'Unable to process document - missing document information');
			console.error('Mark complete failed: currentStatus or currentTemplate is null', {
				currentStatus,
				currentTemplate,
			});
			return;
		}

		setIsUploading(true);
		try {
			const { documentViewer, annotationManager } = instance.current.Core;
			const doc = documentViewer.getDocument();

			if (!doc) {
				showToast('error', 'No document loaded');
				console.error('Mark complete failed: No document loaded');
				setIsUploading(false);
				return;
			}

			// Get the document data with filled form fields and annotations
			const xfdfString = await annotationManager.exportAnnotations();
			const data = await doc.getFileData({ xfdfString });
			const arr = new Uint8Array(data);
			const blob = new Blob([arr], { type: 'application/pdf' });
			const file = new File([blob], `${currentStatus.name}.pdf`, { type: 'application/pdf' });

			await uploadOnboardingPhaseTwoDocument(file, onboardingId, currentStatus.template_id);
			showToast('success', 'Document submitted successfully');

			// Build updated statuses so we can check next/all logic against fresh data
			const updatedStatuses = localDocStatuses.map((doc, idx) =>
				idx === currentDocIndex
					? { ...doc, status: 'completed', path: `${currentStatus.name}.pdf` }
					: doc
			);

			// Update local state (will apply on next render)
			setLocalDocStatuses(updatedStatuses);

			// Find next pending document and navigate to it
			const nextPendingIndex = updatedStatuses.findIndex(
				(doc, idx) =>
					idx > currentDocIndex &&
					doc.status !== 'completed' &&
					doc.status !== 'approved'
			);

			if (nextPendingIndex !== -1) {
				setCurrentDocIndex(nextPendingIndex);
			} else {
				// Check if ALL docs (including before current) are done
				const allCompleted = updatedStatuses.every(
					(doc) => doc.status === 'completed' || doc.status === 'approved'
				);
				if (allCompleted) {
					// All documents done - advance to Complete step without full reload
					onAllDocumentsComplete();
				} else {
					// There are still pending docs before current, wrap around to first pending
					const firstPendingIndex = updatedStatuses.findIndex(
						(doc) => doc.status !== 'completed' && doc.status !== 'approved'
					);
					if (firstPendingIndex !== -1) {
						setCurrentDocIndex(firstPendingIndex);
					}
				}
			}
		} catch (error) {
			showToast('error', 'Failed to submit document');
			console.error('Upload error:', error);
		} finally {
			setIsUploading(false);
		}
	};

	const pendingDocuments = documents.filter(
		(doc) => doc.status !== 'completed' && doc.status !== 'approved'
	);
	const completedCount = documents.length - pendingDocuments.length;
	const progressPercent =
		documents.length > 0 ? Math.round((completedCount / documents.length) * 100) : 0;

	const getStatusIcon = (status: string) => {
		if (status === 'completed' || status === 'approved') {
			return <CheckCircleIcon color="success" />;
		}
		return <PendingIcon color="warning" />;
	};

	const getStatusChip = (status: string) => {
		if (status === 'completed' || status === 'approved') {
			return <Chip label="Completed" color="success" size="small" />;
		}
		return <Chip label="Pending" color="warning" size="small" />;
	};

	return (
		<Box>
			{/* Status Header */}
			<Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff' }}>
				<Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
					Review Documents
				</Typography>
				<Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
					Please review and complete all required documents below.
				</Typography>

				{/* Progress Bar */}
				<Box sx={{ mt: 2 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
						<Typography variant="body2" color="text.secondary">
							Progress: {completedCount} of {documents.length} completed
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{progressPercent}%
						</Typography>
					</Box>
					<LinearProgress
						variant="determinate"
						value={progressPercent}
						sx={{ height: 8, borderRadius: 4 }}
					/>
				</Box>
			</Paper>

			{/* Main Content: Document List Sidebar + PDF Viewer */}
			<Grid container spacing={3}>
				{/* Left Sidebar - Document List/Index */}
				<Grid item xs={12} md={4} lg={3}>
					<Paper sx={{ backgroundColor: '#fff', position: 'sticky', top: 16 }}>
						<Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
							<Typography variant="h6" sx={{ fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
								Documents
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{documents.length} document(s) • {pendingDocuments.length} pending
							</Typography>
						</Box>
						<List sx={{ maxHeight: '600px', overflow: 'auto' }}>
						{documents.map((doc, idx) => {
							const docStatus = doc.status || 'pending';
							return (
								<React.Fragment key={doc.template_id || idx}>
										<ListItem disablePadding>
											<ListItemButton
												selected={idx === currentDocIndex}
												onClick={() => setCurrentDocIndex(idx)}
												sx={{
													'&.Mui-selected': {
														backgroundColor: 'rgba(68, 99, 136, 0.08)',
														'&:hover': {
															backgroundColor: 'rgba(68, 99, 136, 0.12)',
														},
													},
												}}
											>
												<ListItemIcon>{getStatusIcon(docStatus)}</ListItemIcon>
												<ListItemText
													primary={doc.name || doc.path}
													secondary={getStatusChip(docStatus)}
													secondaryTypographyProps={{ component: 'div' }}
												/>
											</ListItemButton>
										</ListItem>
										{idx < documents.length - 1 && <Divider />}
									</React.Fragment>
								);
							})}
						</List>
					</Paper>
				</Grid>

				{/* Right Side - PDF Viewer */}
				<Grid item xs={12} md={8} lg={9}>
					<Paper sx={{ p: 2, mb: 3, backgroundColor: '#fff' }}>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
							{currentStatus?.name || 'Document'}
						</Typography>
					<div
						ref={viewer}
						style={{
							height: '700px',
							width: '100%',
							border: '1px solid #e0e0e0',
							borderRadius: 8,
						}}
					/>
					{/* Action Buttons */}
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
						<Button 
							variant="contained" 
							sx={{ backgroundColor: 'rgb(68, 99, 136)' }}
							onClick={handleMarkAsComplete}
							disabled={!isViewerReady || isUploading || currentStatus?.status === 'completed' || currentStatus?.status === 'approved'}
						>
							{isUploading ? 'Submitting...' : 'Mark as Complete'}
						</Button>
					</Box>
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);
}
